import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTaskDTO } from '../../hooks/firebase/TaskDto'
import useSharedCollection from '../../firebase/useSharedCollection'
import useLocationFilter from '../../hooks/useLocationFilter'
import hasLocationFilter from '../../helpers/dashboardHelpers/hasLocationFilter'
import { Auth } from '../../StateManagment/Auth'
import { CalendarView } from '../calendar/CalendarView'
import { personaColorById } from '../../helpers/calendar/personaAgendaColors'
import DashboardCardOptions from './DashboardCardOptions'
import DashboardFilterBar from './DashboardFilterBar'
import { useHistory } from 'react-router-dom'
import useSettings from '../../hooks/Settings.jsx'
import { client } from '../../hooks/Client'
import { householdAgendaPath, householdPlanningEnabled } from '../../helpers/board/resolveBoardView'
import useAdressStatusById from '../../hooks/useAdressStatusById'
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined'
import DashboardLocationFilter from './DashboardLocationFilter'
import usePersonaLocations from '../../hooks/usePersonaLocations'
import useAgendaSchedule from '../../hooks/calendar/useAgendaSchedule'

const DashboardAgendaCard = ({ item }) => {
    const history = useHistory()
    const settings = useSettings()
    // Context
    const [auth] = useContext(Auth)

    // Hooks
    const taskDTO = useTaskDTO()
    const personas = usePersonaLocations()
    const locations = useSharedCollection('Locations')
    // De status van het huishouden als bolletje in het blok van een huisbezoek,
    // alleen met de module Huishoudens: zonder die module is er geen huisbezoek
    // en betaalt niemand de lezing (verzoek BuurtKlusBedrijf).
    const adresses = useSharedCollection(householdPlanningEnabled(settings) ? 'Adresses' : null)
    const adressStatusById = useAdressStatusById(adresses, settings.adressStatusConfig)
    const { filteredItems, selectedLocationId, setSelectedLocationId } =
        useLocationFilter(personas, { item })

    // State
    const [docs, setDocs] = useState([])
    const [dateRange, setDateRange] = useState({ start: new Date(), end: new Date() })
    const [refreshCounter, setRefreshCounter] = useState(0)

    const view = item?.AgendaView || 'list'

    /** @type {React.RefObject<HTMLDivElement>} */
    const wrapRef = useRef(null)

    // FullCalendar krijgt een GETAL en geen '100%'. Geen enkele ouder van de
    // kalender heeft een opgegeven hoogte -- de kaart is wel 600px, maar
    // `.new-dashboard-card-content` is een flex-item (`flex: 1`) en het
    // <fieldset> van ReadOnlyGuard ertussen heeft `auto` -- dus lost 100% op
    // naar 0. In de lijstweergave valt dat niet op, maar in de dagweergave
    // tekent FullCalendar dan alleen de kopbalk: de kaart lijkt leeg terwijl er
    // gewoon afspraken staan. Gemeten bij BuurtKlusBedrijf: kalender 60px hoog,
    // het vlak eronder 0px, met één afspraak in de gegevens.
    // Updateverzoek BuurtKlusBedrijf, 2026-09-21.
    // AI-CONTEXT: See docs/pitfalls.md#ui-chart-renders-blank-after-move
    const [calendarHeight, setCalendarHeight] = useState(320)

    const meet = useCallback(() => {
        const blok = wrapRef.current
        if (!blok) return
        // De ruimte die dit blok binnen de kaart krijgt. `clientHeight` en niet
        // een eigen rekensom: de kaart bepaalt zijn eigen hoogte, en die kan
        // per dashboard verschillen.
        const beschikbaar = blok.clientHeight
        if (beschikbaar > 0) setCalendarHeight(beschikbaar)
    }, [])

    useLayoutEffect(() => { meet() }, [meet, view])

    useEffect(() => {
        const blok = wrapRef.current
        // jsdom kent ResizeObserver niet; daar blijft het bij de meting hierboven.
        if (!blok || typeof ResizeObserver === 'undefined') return undefined

        const observer = new ResizeObserver(() => meet())
        observer.observe(blok)
        return () => observer.disconnect()
    }, [meet])

    // useEffect
    useEffect(() => {
        const unsubscribe = taskDTO.queryBy({
            query: (q) => q,
            dateStart: dateRange.start,
            dateEnd: dateRange.end,
            onUpdate: setDocs,
        })

        return () => unsubscribe()
    }, [dateRange, refreshCounter]) // eslint-disable-line

    // Dezelfde regel als de grote agenda: het rooster van actieven, en van
    // uitgestroomden tot en met hun uitstroomdatum. Tot 2026-10-16 toonde de
    // kaart het rooster van iedereen, ook van wie al lang weg was.
    const { visibleDocs, scheduleEndByPersonaId } = useAgendaSchedule(personas, docs, dateRange.start)

    const filteredDocs = useMemo(() => {
        if (!selectedLocationId) return visibleDocs
        const allowedIds = new Set(filteredItems.map((p) => p?.ID).filter(Boolean))
        return visibleDocs.filter((d) => allowedIds.has(d?.PersonaID))
    }, [visibleDocs, filteredItems, selectedLocationId])

    // Eigen agendakleur per persona — dezelfde kleur als in de grote agenda.
    const personaColors = useMemo(
        () => personaColorById(Array.isArray(personas) ? personas : []),
        [personas]
    )

    return (
        <>
            <DashboardFilterBar
                onClear={() => setSelectedLocationId('')}
                activeFilterCount={selectedLocationId ? 1 : 0}
                hasFilters={hasLocationFilter(locations)}
            >
                <DashboardLocationFilter
                    selectedLocationId={selectedLocationId}
                    onChange={setSelectedLocationId}
                />
            </DashboardFilterBar>
            <div className='dashboard-agenda-card__calendar' ref={wrapRef}>
            <CalendarView
                docs={filteredDocs}
                personaColorById={personaColors}
                adressStatusById={adressStatusById}
                scheduleEndByPersonaId={scheduleEndByPersonaId}
                userId={auth?.ID}
                onDateRangeChange={setDateRange}
                onStaleData={() => setRefreshCounter(c => c + 1)}
                view={view}
                height={calendarHeight}
                headerToolbar={{
                    left: 'title',
                    center: '',
                    right: 'prev,next',
                }}
            />
            </div>
            {/* Ingang naar 'Huishoudens inplannen' als icoon in de onderbalk,
                naast 'Weergave aanpassen' (verzoek BuurtKlusBedrijf): de kaart
                zelf is te krap voor een knop boven de agenda. Alleen met de
                module Huishoudens ('FIXtool'). */}
            <DashboardCardOptions
                item={item}
                extraActions={householdPlanningEnabled(settings) ? [{
                    id: 'householdPlanning',
                    label: 'Huishoudens inplannen',
                    Icon: EditCalendarOutlinedIcon,
                    onClick: () => history.push(householdAgendaPath(client)),
                }] : []}
            />
        </>
    )
}

export default DashboardAgendaCard
